import { useRef, useEffect } from "react";
import { useDBLog } from "hooks/useDBLog";
import { sentryCapture } from "../sentry/sentry";

interface UseStandardYouTubeEmbedOptions {
  enabled: boolean;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  videoId: string;
  onReady?: () => void;
  onStateChange?: (state: number) => void;
  onEnd?: () => void;
  currentTimeRef: React.MutableRefObject<number>;
  playbackRateRef: React.MutableRefObject<number>;
  playerStateRef: React.MutableRefObject<number>;
}

export const useStandardYouTubeEmbed = ({
  enabled,
  iframeRef,
  videoId,
  onReady,
  onStateChange,
  onEnd,
  currentTimeRef,
  playbackRateRef,
  playerStateRef
}: UseStandardYouTubeEmbedOptions) => {
  const dblog = useDBLog();
  const playerRef = useRef<any>(null);
  const callbacksRef = useRef({ onReady, onStateChange, onEnd });

  useEffect(() => {
    callbacksRef.current = { onReady, onStateChange, onEnd };
  }, [onReady, onStateChange, onEnd]);

  useEffect(() => {
    if (!enabled || !iframeRef.current) {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error("Failed to destroy YouTube player", error);
        }
        playerRef.current = null;
      }
      return;
    }

    const initPlayer = () => {
      if (!iframeRef.current || playerRef.current) return;
      try {
        playerRef.current = new (window as any).YT.Player(iframeRef.current, {
          playerVars: { enablejsapi: 1, playsinline: 1 },
          events: {
            onReady: () => {
              callbacksRef.current.onReady?.();
              currentTimeRef.current = playerRef.current.getCurrentTime();
              playbackRateRef.current = playerRef.current.getPlaybackRate();
              playerStateRef.current = playerRef.current.getPlayerState();
            },
            onStateChange: (e: any) => {
              currentTimeRef.current = playerRef.current.getCurrentTime();
              playerStateRef.current = e.data;
              callbacksRef.current.onStateChange?.(e.data);
              if (e.data === (window as any).YT.PlayerState.ENDED) {
                callbacksRef.current.onEnd?.();
              }
            },
            onPlaybackRateChange: (e: any) => {
              playbackRateRef.current = e.data;
            },
            onError: (e: any) => {
              dblog("youtube-error", "Standard embed also failed", { code: e.data, videoId });
              sentryCapture("YouTube error", { code: e.data, videoId });
            }
          }
        });
      } catch (error) {
        console.error("Failed to initialize YouTube player", error);
      }
    };

    if ((window as any).YT?.Player) {
      initPlayer();
    } else {
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(tag);
      }
      (window as any).onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error("Failed to destroy YouTube player", error);
        }
        playerRef.current = null;
      }
    };
  }, [enabled, iframeRef, videoId, dblog, currentTimeRef, playbackRateRef, playerStateRef]);

  return playerRef;
};
